import { AsyncPipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { CardMode } from '@components/card/card.component';
import { MdsCheckboxComponent, MdsTextFieldComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Site, SiteVisitStipendBase } from '@models/site';
import { VisitName } from '@models/study';
import { rxjsEffect } from '@utility/utility';
import { combineLatest, filter, map, startWith, switchMap, tap } from 'rxjs';
import { SingleStipendVisitFormGroup } from './single-stipend-visit.form';

@Component({
    selector: 'medpace-single-stipend-visit',
    imports: [MdsTextFieldComponent, MdsCheckboxComponent, AsyncPipe, DecimalPipe, dateFormatPipe],
    templateUrl: './single-stipend-visit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleStipendVisitComponent {
    formGrp = input.required<SingleStipendVisitFormGroup>();
    existingStipend = input<SiteVisitStipendBase>();
    displayName = input.required<string>();
    studyVisit = input.required<VisitName>();
    mode = input.required<CardMode>();

    formGrp$ = toObservable(this.formGrp);
    existingStipend$ = toObservable(this.existingStipend);
    hasStipend$ = this.formGrp$.pipe(
        switchMap((formGroup) =>
            formGroup.controls.hasStipend.valueChanges.pipe(startWith(formGroup.controls.hasStipend.value))
        )
    );
    modifierShortenedName$ = this.existingStipend$.pipe(
        filter(Boolean),
        map((stipend) => {
            if (!stipend.modifierFullName) return 'Unknown';

            return stipend.modifierFullName
                .replace('  ', ' ') // middlenames can sometimes have double spaces
                .split(' ') // split into words
                .map((value, index, array) => {
                    if (index < array.length - 1)
                        return value.substring(0, 1) + '.'; // if not last element, make it an initial
                    else return value; // if lastname, return it
                    // result is like "J.D.Doe" or "J.Doe" if there is no middlename
                })
                .join('');
        })
    );

    patch$ = rxjsEffect(() =>
        combineLatest({
            existingStipend: this.existingStipend$,
            formGroup: this.formGrp$,
        }).pipe(
            tap(({ existingStipend, formGroup }) => {
                if (existingStipend) {
                    formGroup.patchValue({
                        hasStipend: true,
                        amount: existingStipend.amount,
                    });
                } else {
                    formGroup.patchValue({
                        hasStipend: false,
                        amount: null,
                    });
                }
            })
        )
    );

    switchAmount$ = rxjsEffect(() =>
        combineLatest({
            hasStipend: this.hasStipend$,
            formGroup: this.formGrp$,
            mode: toObservable(this.mode),
        }).pipe(
            filter(({ mode }) => mode === 'edit'),
            tap(({ hasStipend, formGroup }) => {
                if (hasStipend) {
                    formGroup.controls.amount.enable({ breakPersistence: true });
                } else formGroup.controls.amount.disable({ persistent: true });
            })
        )
    );

    push(site: Site) {
        const value = this.formGrp().getRawValue();
        const existingStipend = this.existingStipend();

        if (!existingStipend) {
            //create
            if (!value.hasStipend)
                return site; //unchanged
            else {
                //added
                site.siteVisitStipends.push({
                    amount: value.amount!,
                    visitId: this.studyVisit().id,
                    siteId: site.id,
                });
            }
        } else {
            //update
            if (!value.hasStipend) {
                //removed
                site.siteVisitStipends = site.siteVisitStipends.filter((stipend) => stipend !== existingStipend);
            } else {
                //modified
                existingStipend.amount = value.amount!;
            }
        }

        return site;
    }
}
